import { useCallback, useEffect, useMemo, useState } from "react";
import type { DetailField, DetailFieldsSection } from "@/api/types";

type DetailSection = DetailFieldsSection;

interface PersistedConfig {
  /** Operator-selected visible field keys. Locked keys aren't
   *  stored — they're always-on at read time, regardless. */
  visible: string[];
  /** Custom display order. Keys that aren't in this array fall
   *  back to the descriptor's natural order so newly-added fields
   *  appear at the bottom on the next render. */
  order: string[];
}

/**
 * Per-section field-customisation hook. Tracks both visibility
 * and display order, persisted to localStorage so each operator
 * keeps their layout across reloads.
 *
 * Storage shape (one entry per section per project slug):
 *   prism.{slug}.detail.{sectionKey} =
 *     { "visible": ["domain","sector",…], "order": ["sector","city",…] }
 *
 * Locked fields are deliberately excluded from `visible` (they're
 * always-on at read time) but they ARE included in `order`, so
 * operators can position scoring fields wherever they want.
 *
 * Backward compat: an older `prism.{slug}.detail.{sectionKey}.visible`
 * key from the visibility-only release is read on first mount, and
 * the result is migrated into the new structured entry on the
 * first persist.
 */
export function useFieldVisibility(slug: string, section: DetailSection) {
  const [config, setConfig] = useState<PersistedConfig>(() =>
    initialConfig(slug, section),
  );

  // Re-seed when the slug or section identity changes (e.g. project
  // switch in the same browser tab).
  useEffect(() => {
    setConfig(initialConfig(slug, section));
  }, [slug, section]);

  const persist = useCallback(
    (next: PersistedConfig) => {
      try {
        window.localStorage.setItem(
          storageKey(slug, section.key),
          JSON.stringify(next),
        );
      } catch {
        // QuotaExceeded / disabled storage / private mode — silent.
      }
    },
    [slug, section.key],
  );

  const toggle = useCallback(
    (key: string) => {
      setConfig((prev) => {
        const next: PersistedConfig = {
          ...prev,
          visible: prev.visible.includes(key)
            ? prev.visible.filter((k) => k !== key)
            : [...prev.visible, key],
        };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const reorder = useCallback(
    (activeKey: string, overKey: string) => {
      if (activeKey === overKey) return;
      setConfig((prev) => {
        const oldIndex = prev.order.indexOf(activeKey);
        const newIndex = prev.order.indexOf(overKey);
        if (oldIndex < 0 || newIndex < 0) return prev;
        const order = [...prev.order];
        const [moved] = order.splice(oldIndex, 1);
        order.splice(newIndex, 0, moved);
        const next: PersistedConfig = { ...prev, order };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const reset = useCallback(() => {
    const next: PersistedConfig = defaultConfig(section);
    setConfig(next);
    persist(next);
  }, [persist, section]);

  /** Replace the current config wholesale — used when applying a
   *  saved layout view. The incoming visible/order arrays are
   *  filtered to known field keys so an old saved view with a
   *  removed field key doesn't throw the renderer off. */
  const applyConfig = useCallback(
    (incoming: Partial<PersistedConfig>) => {
      const known = new Set(section.fields.map((f) => f.key));
      const visible =
        incoming.visible
          ?.filter((k): k is string => typeof k === "string")
          .filter((k) => known.has(k)) ?? defaultConfig(section).visible;
      const order =
        incoming.order
          ?.filter((k): k is string => typeof k === "string")
          .filter((k) => known.has(k)) ?? defaultConfig(section).order;
      const next: PersistedConfig = { visible, order };
      setConfig(next);
      persist(next);
    },
    [persist, section],
  );

  // Locked-field set, computed once per section — folded into
  // isVisible() so a stale order/visibility entry can never hide a
  // scoring field. Kept O(1) on the render path.
  const lockedSet = useMemo(
    () =>
      new Set(section.fields.filter((f) => f.locked).map((f) => f.key)),
    [section],
  );

  const isVisible = useCallback(
    (key: string) =>
      lockedSet.has(key) || config.visible.includes(key),
    [lockedSet, config.visible],
  );

  // Effective ordering: stored order first, then any descriptor
  // fields that aren't in the stored order (newly-added fields land
  // at the bottom). Resolves each key back to the descriptor so the
  // popover and the card both render labels and locked-state from a
  // single source of truth.
  const orderedFields = useMemo<DetailField[]>(() => {
    const byKey = new Map(section.fields.map((f) => [f.key, f]));
    const seen = new Set<string>();
    const out: DetailField[] = [];
    for (const k of config.order) {
      const f = byKey.get(k);
      if (f) {
        out.push(f);
        seen.add(k);
      }
    }
    for (const f of section.fields) {
      if (!seen.has(f.key)) out.push(f);
    }
    return out;
  }, [section, config.order]);

  // Set form for fast lookups in the popover and card render path.
  const visibleSet = useMemo(
    () => new Set(config.visible),
    [config.visible],
  );

  return {
    /** Raw persisted config — exposed so save-as-view can include
     *  the section's visibility + order in the saved payload. */
    config,
    visibleSet,
    isVisible,
    toggle,
    reorder,
    reset,
    applyConfig,
    orderedFields,
  };
}

function storageKey(slug: string, sectionKey: string): string {
  return `prism.${slug}.detail.${sectionKey}`;
}

function legacyVisibleKey(slug: string, sectionKey: string): string {
  return `prism.${slug}.detail.${sectionKey}.visible`;
}

function initialConfig(slug: string, section: DetailSection): PersistedConfig {
  // Try the structured entry first.
  try {
    const raw = window.localStorage.getItem(storageKey(slug, section.key));
    if (raw) {
      const parsed = JSON.parse(raw);
      const visible = Array.isArray(parsed?.visible)
        ? parsed.visible.filter((s: unknown): s is string => typeof s === "string")
        : null;
      const order = Array.isArray(parsed?.order)
        ? parsed.order.filter((s: unknown): s is string => typeof s === "string")
        : null;
      if (visible || order) {
        const fallback = defaultConfig(section);
        return {
          visible: visible ?? fallback.visible,
          order: order ?? fallback.order,
        };
      }
    }
  } catch {
    /* fall through */
  }

  // Backward-compat: read the v1 visibility-only entry, drop the
  // legacy key on first migration so the storage stays tidy.
  try {
    const legacyRaw = window.localStorage.getItem(
      legacyVisibleKey(slug, section.key),
    );
    if (legacyRaw) {
      const parsed = JSON.parse(legacyRaw);
      if (Array.isArray(parsed)) {
        const visible = parsed.filter(
          (s: unknown): s is string => typeof s === "string",
        );
        try {
          window.localStorage.removeItem(legacyVisibleKey(slug, section.key));
        } catch {
          /* ignore */
        }
        return { visible, order: defaultConfig(section).order };
      }
    }
  } catch {
    /* fall through */
  }

  return defaultConfig(section);
}

function defaultConfig(section: DetailSection): PersistedConfig {
  return {
    visible: section.fields
      .filter((f) => f.default_visible !== false && !f.locked)
      .map((f) => f.key),
    order: section.fields.map((f) => f.key),
  };
}
